import { AuthContext } from "../Context/AuthContext";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { TextField } from "@mui/material";
import { useContext, useState, useEffect } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import AppleLogin from "react-apple-login";
import axios from "axios";
import axiosInstance from '../utils/axiosConfig'; // Add axiosInstance import
import Navbar from "../components/Navbar";
import { FiCopy, FiCheck } from "react-icons/fi";
import { FaApple } from "react-icons/fa";
import TenantContext from "../Context/TenantContext";

const Register = () => {
  const { isAuthenticated, setIsAuthenticated, setUser, setToken } = useContext(AuthContext);
  const [email, setEmail] = useState("");
  const [copiedWebUrl, setCopiedWebUrl] = useState(false);
  const webLoginUrl = "https://bh.healgpt.co/login";

  const { tenant = {} } = useContext(TenantContext) || {};

  const handleCopyWebUrl = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(webLoginUrl);
        setCopiedWebUrl(true);
        setTimeout(() => setCopiedWebUrl(false), 1500);
      }
    } catch (e) {
      console.error("Copy failed", e);
    }
  };

  const navigateTo = useNavigate();

  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => handleGoogleCode(tokenResponse.code),
    flow: "auth-code",
    scope: "email profile openid https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email",
  });

  useEffect(() => {
    if (!import.meta.env.VITE_APP_APPLE_CLIENTID) return;
    setTimeout(() => {
      window.AppleID?.auth.init({
        clientId: import.meta.env.VITE_APP_APPLE_CLIENTID,
        redirectURI: import.meta.env.VITE_APP_APPLE_REDIRECT_URL,
        usePopup: true,
      });
    }, 500);
  }, []);

  /**
   *
   * @param {string} code
   */
  async function handleGoogleCode(code) {
    try {
      const requestData = { 
        code, 
        role: "Patient",
        redirect_uri: window.location.origin, // Add the redirect_uri parameter
        organization: import.meta.env.VITE_TENANT_OVERRIDE || 'vikramaura-hospitals' // Send tenant organization
      };
      
      console.log("[Google SSO] Sending request data:", requestData);
      
      const [data, axiosError] = await axiosInstance
        .post(
          "/user/google/callback", // Use relative path since baseURL is configured
          requestData,
          {
            withCredentials: true,
            headers: { "Content-Type": "application/json" },
          }
        )
        .then(({ data }) => [data, undefined])
        .catch((err) => [undefined, err]);

      if (axiosError) {
        if (axiosError.response.status === 403) {
          navigateTo("/register/get-details", {
            state: {
              ...axiosError.response.data.data,
            },
          });
          toast.info(axiosError.response.data.message);
          return;
        }

        toast.error(axiosError.response.data.message);
        return;
      }

      if (data.new_user === true) {
        navigateTo("/register/get-details", {
          state: {
            email: data?.data?.email,
            firstName: data?.data?.firstName,
            lastName: data?.data?.lastName,
          },
        });
        return;
      }

      toast.success(data.message);
      setIsAuthenticated(true);
      setUser(data.user);
      setToken(data.token);
      navigateTo("/");
      setEmail("");
    } catch (err) {
      console.error(err);
      toast.error("Some Error Occured");
    }
  }

  async function appleResponse(response) {
    if (response.error) {
      toast.error("Problem with apple signin");
      console.error(response.error);
      return;
    }
    const [data, error] = await axios
      .post(import.meta.env.VITE_APP_BASE_API + import.meta.env.VITE_APP_API_VERSION + "/user/apple/callback", {
        id_token: response.authorization.id_token,
        firstName: response?.user?.name?.firstName,
        lastName: response?.user?.name?.lastName,
        email: response?.user?.email,
        role: "Patient",
      })
      .then((res) => [res, undefined])
      .catch((err) => [undefined, err]);

    if (error) {
      if (error.response.status === 403) {
        navigateTo("/register/get-details?email=true&appleSub=" + error.response?.data?.data?.appleSub, {
          state: {
            ...error.response.data.data,
          },
        });
        toast.info(error.response.data.message);
        return;
      }

      toast.error(error.response.data.message);
      return;
    }

    if (data.data.new_user === true) {
      if (!data.data.data.email) {
        navigateTo("/register/get-details?email=true&appleSub=" + data.data.data.appleSub);
        return;
      }
      navigateTo("/register/get-details", {
        state: {
          email: data?.data?.email,
          firstName: data?.data?.firstName,
          lastName: data?.data?.lastName,
        },
      });
      return;
    }

    toast.success(data.message);
    setIsAuthenticated(true);
    setUser(data.user);
    setToken(data.token);
    navigateTo("/");
    setEmail("");
  }

  async function handleAccountCheck(e) {
    e.preventDefault();
    try {
      const [_data, axiosError] = await axios
        .post(
          import.meta.env.VITE_APP_BASE_API + import.meta.env.VITE_APP_API_VERSION + "/user/check-user",
          { email, role: "Patient" },
          {
            withCredentials: true,
            headers: { "Content-Type": "application/json" },
          }
        )
        .then(({ data }) => [data, undefined])
        .catch((err) => [undefined, err]);

      if (axiosError) {
        toast.error(axiosError.response.data.message);
        return;
      }

      navigateTo("/register/get-details?password=true", {
        state: {
          email: email.toLowerCase(),
        },
      });
    } catch (err) {
      console.error(err);
      toast.error("Some Error Occured");
    }
  }

  if (isAuthenticated) {
    return <Navigate to={"/"} />;
  }

  return (
    <>
      <Navbar />
      <div className="py-8 flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
        <div className="w-full max-w-md space-y-2">
          {/* Logo and Title Section */}
          <div className="text-center">
            <h2 className="text-3xl font-semibold text-gray-900 mb-2">
              Create your account
            </h2>
            <p className="text-gray-600">
              Smart Nurse
            </p>
          </div>

          {/* Registration Form */}
          <div className="bg-white py-8 px-4 shadow-2xl rounded-2xl space-y-8 sm:px-10">
            {/* Social Login Buttons */}
            <div className="space-y-4">
              <button
                onClick={googleLogin}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>

              {import.meta.env.VITE_APP_APPLE_CLIENTID && (
              <AppleLogin
                clientId={import.meta.env.VITE_APP_APPLE_CLIENTID}
                redirectURI={import.meta.env.VITE_APP_APPLE_REDIRECT_URL}
                usePopup={true}
                callback={appleResponse}
                scope="email name"
                responseMode="form_post"
                render={(renderProps) => (
                  <button
                    onClick={renderProps.onClick}
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    <FaApple className="w-5 h-5" />
                    Continue with Apple
                  </button>
                )}
              />
              )}
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">Or</span>
              </div>
            </div>

            {/* Email Form */}
            <form onSubmit={handleAccountCheck} className="space-y-6">
              <div>
                <TextField
                  fullWidth
                  label="Email address"
                  variant="outlined"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-full"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                    },
                  }}
                />
              </div>

              <div className="flex items-center justify-end">
                <Link
                  to="/login"
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  Already have an account?
                </Link>
              </div>

              <button
                type="submit"
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Create Account
              </button>
            </form>
            {/* Web Login QR Code */}
            <div className="mt-6 rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-gray-50 p-5 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Web Login</h3>
                  <p className="text-sm text-gray-500">Scan to open the web login page</p>
                </div>
                <button
                  type="button"
                  onClick={handleCopyWebUrl}
                  className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
                  aria-label="Copy web login link"
                >
                  {copiedWebUrl ? (
                    <>
                      <FiCheck className="text-green-600" /> Copied
                    </>
                  ) : (
                    <>
                      <FiCopy /> Copy link
                    </>
                  )}
                </button>
              </div>

              <div className="mt-4 flex justify-center">
                <div className="relative rounded-xl bg-white p-3 border border-gray-200 shadow-inner">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&ecc=H&margin=20&data=${encodeURIComponent(webLoginUrl)}`}
                    alt="QR code for web login"
                    className="h-48 w-48 block"
                    loading="lazy"
                    decoding="async"
                  />
                  {/* Center logo with a white mask to keep QR scannable */}
                  <div
                    className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-md flex items-center justify-center"
                    style={{ width: '48px', height: '48px' }}
                    aria-hidden="true"
                  >
                    <img
                      src={tenant.qrLogo}
                      alt=""
                      className="h-8 w-8 select-none"
                      draggable="false"
                      role="presentation"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-4 text-center">
                <a
                  href={webLoginUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-blue-600 hover:underline break-all"
                >
                  {webLoginUrl}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
